import styled from 'styled-components';

import { device } from 'config/device';

export const THwrapper = styled.div`
  width: max-content;
  grid-gap: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  fill: ${(props) => props.theme.primary};
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.6;
  }
`;

export const GroupWrapper = styled.div`
  display: flex;
  transition: opacity 0.2s ease-in-out;
  :hover {
    opacity: 0.6;
  }
`;

type TablePageWrapperProps = {
  $isClickable?: boolean;
  $offset?: number;
  $height?: number;
};

export const TablePageWrapper = styled.div<TablePageWrapperProps>`
  height: 100%;
  font-size: ${(props) => props.theme.small};
  font-weight: 500;
  border: 1px solid #d4d4d4;
  border-radius: 12px;
  padding: 16px 32px;
  box-shadow: ${(props) => props.theme.shadow[3]};

  @media ${device.mobile} {
    padding: 8px 12px;
    height: calc(100% - 50px);
    overflow-x: hidden; /* Prevent horizontal scrolling */
    max-width: 100vw; /* Ensure it doesn't exceed viewport width */
  }

  /* Short landscape viewports (e.g. phones rotated): the outer ListPageWrapper
     is the scroll area, so this wrapper grows to content (no nested scroll).
     Drop top padding so the sticky header sits flush at the top. */
  @media (max-height: 550px) and (orientation: landscape) {
    height: auto;
    overflow: visible;
    padding-top: 0;
    border: none;
    box-shadow: none;
  }

  .container {
    width: 100%;
    max-height: ${(props) => {
      if (props.$height) return `${props.$height}rem`;
      return `calc(100% - ${props.$offset || 0}rem)`;
    }};
    position: relative;
    overflow: auto;
    @media ${device.mobile} {
      max-height: ${(props) => {
        if (props.$height) return `${props.$height}rem`;
        return `calc(100% - ${(props.$offset || 0) + 2 || 0}rem)`;
      }};
      overflow-x: hidden; /* Prevent horizontal scrolling on mobile */
      overflow-y: auto; /* Allow vertical scrolling only */
    }
    /* Short landscape: both vertical and horizontal scroll live on the outer
       ListPageWrapper (so the header section scrolls away and the sticky thead
       pins). This container just grows to content. */
    @media (max-height: 550px) and (orientation: landscape) {
      max-height: none;
      overflow: visible;
    }
  }
  table {
    width: 100%;
    padding: 1rem;
    text-align: left;
    border-collapse: collapse;

    @media ${device.mobile} {
      padding: 0.5rem;
      min-width: 100%; /* Ensure table takes full width */
      table-layout: fixed; /* Fixed layout for better mobile control */
    }

    /* Short landscape: no top padding so the sticky header is flush and no
       scrolled rows peek above it. */
    @media (max-height: 550px) and (orientation: landscape) {
      padding-top: 0;
    }
  }
  [active-row='true'] {
    background-color: ${(props) => `${props.theme.special}1A`} !important;
  }
  [is-grouped='true'] {
    background-color: ${(props) => `${props.theme.special}1A`};
  }
  [is-placeholder='true'] {
    text-align: right;
    background-color: ${(props) => `${props.theme.error}1A`};
  }
  [is-float='true'] {
    text-align: right;
  }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ffffff;
    tr {
      font-size: ${(props) => props.theme.small};
      background: #ffffff;

      :after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => props.theme.grayLight3}; /* Border color */
        pointer-events: none; /* Ensures it doesn’t interfere with table interactions */
      }
    }
    th {
      padding: 1rem 1.5rem;
      font-weight: 500;
      font-size: 1.4rem;
      color: #404040;
      background: #ffffff;
      svg {
        color: ${(props) => props.theme.special};
        width: 20px;
        height: 20px;
      }
    }
  }
  tbody {
    cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
    tr {
      &:nth-child(even) {
        background-color: #f5f5f5;
      }
      &:nth-child(odd) {
        background-color: #fff;
      }
      td:first-child {
        border-top-left-radius: 8px; /* Top left corner of first column */
        border-bottom-left-radius: 8px; /* Bottom left corner of first column */
      }
      td:last-child {
        border-top-right-radius: 8px; /* Top right corner of last column */
        border-bottom-right-radius: 8px; /* Bottom right corner of last column */
      }
    }
    tr:nth-child(even) {
      background-color: #f5f5f5;
    }
    tr:nth-child(odd) {
      background-color: #fff;
    }
    td {
      padding: 1.5rem 1.2rem;
      color: #525252;
      svg {
        color: ${(props) => props.theme.special};
      }

      @media ${device.mobile} {
        padding: 0.8rem 0.5rem;
        font-size: 0.9rem;
        word-wrap: break-word; /* Allow text to wrap */
        overflow-wrap: break-word; /* Break long words */
        max-width: 0; /* Allow columns to shrink */
      }
    }
  }
`;

export const EmptyContainer = styled.div`
  padding: 5rem 0;
  text-align: center;
  font-size: ${(props) => props.theme.medium};
  color: ${(props) => props.theme.error};
`;

type MemoryTableWrapperProps = {
  $isClickable?: boolean;
  $offset?: number;
  $height?: number;
};

export const MemoryTableWrapper = styled.div<MemoryTableWrapperProps>`
  width: 100%;
  height: 100%;
  .container {
    width: 100%;
    max-height: ${(props) => {
      if (props.$height) return props.$height;
      return `calc(100% - ${props.$offset || 0}rem)`;
    }};
    position: relative;
    overflow: auto;
    padding-right: 10px;
  }

  table {
    width: 100%;
    text-align: left;
    border-collapse: collapse;
    background-color: ${(props) => props.theme.white};
    [active-row='true'] {
      td:nth-child(1) {
        color: ${(props) => props.theme.text};
        // border-left: 1rem solid ${(props) => props.theme.special};
      }
    }
    thead {
      position: sticky;
      top: 0;

      tr {
        background: #ffffff;

        :after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 2px;
          background-color: ${(props) => props.theme.grayLight3}; /* Border color */
          pointer-events: none; /* Ensures it doesn’t interfere with table interactions */
        }
      }
      th {
        transition: opacity 0.2s ease-in-out;
        :hover {
          opacity: 0.5;
        }
      }
    }
    th,
    td {
      font-weight: 500;
      font-size: ${(props) => props.theme.small};
      padding: 1.5rem 0.7rem;
    }
    tbody {
      tr {
        cursor: ${({ $isClickable = true }) => ($isClickable ? 'pointer' : 'auto')};
        color: ${(props) => props.theme.gray};
        td {
          :nth-child(1) {
            // border-left: 1rem solid #fff;
          }
        }
        &:hover {
          color: ${(props) => props.theme.text};
          background-color: ${(props) => `${props.theme.special}1A`} !important;
          td:nth-child(1) {
            // border-left: 1rem solid ${(props) => props.theme.special};
          }
        }

        &:nth-child(even) {
          background-color: #f5f5f5;
        }
        &:nth-child(odd) {
          background-color: #fff;
        }
        td:first-child {
          border-top-left-radius: 8px; /* Top left corner of first column */
          border-bottom-left-radius: 8px; /* Bottom left corner of first column */
        }
        td:last-child {
          border-top-right-radius: 8px; /* Top right corner of last column */
          border-bottom-right-radius: 8px; /* Bottom right corner of last column */
        }
      }
    }
  }
`;

export const MobileTablePageWrapper = styled.div`
  .detailMobileView {
    margin-bottom: 10px;
    border: 1px solid ${(props) => props.theme.special};
    border-radius: 12px;
    padding: 16px;
  }

  @media ${device.tablet} {
    &.mobileList {
      overflow: auto;
      height: 100%;
    }
  }

  /* Short landscape viewports: enable scrolling on the card list even when the
     width exceeds the tablet breakpoint (e.g. large phones rotated). */
  @media (max-height: 550px) and (orientation: landscape) {
    &.mobileList {
      overflow: auto;
      height: 100%;
    }
  }

  .mobileViewList {
    border: 1px solid ${(props) => props.theme.grayLight3};
    border-radius: 12px;
    padding: 8px;
    margin-bottom: 10px;
  }
`;
